import React, { useEffect, useState } from 'react';
import { Form, message, ConfigProvider, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { 
  fetchDeviceTypes, 
  fetchMerchants, 
  fetchStoresByMerchant, 
  createDevice, 
  updateDevice,
  clearStores,
} from './store/devicesSlice';
import { DeviceFormProps, DeviceFormData } from './types';
import { StyledFormItem, PrimaryButton, CancelButton, StyledModal, StyledInput, StyledSelect, StyledFormLabel, ModalContainerGlobalStyle } from './styles';
import { LABELS, VALIDATION_MESSAGES, PLACEHOLDERS, MESSAGES } from './constants';

export const DeviceForm: React.FC<DeviceFormProps> = ({
  open,
  initialDevice,
  onSave,
  onCancel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { deviceTypes, merchants, stores, loading } = useSelector((state: RootState) => state.devices);
  const [form] = Form.useForm();
  const [merchantId, setMerchantId] = useState<string>(initialDevice ? (typeof initialDevice.merchant === 'object' ? initialDevice.merchant.id : initialDevice.merchant) : '');
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isEditMode = !!initialDevice;

  useEffect(() => {
    dispatch(fetchDeviceTypes());
    dispatch(fetchMerchants());
  }, [dispatch]);

  useEffect(() => {
    if (merchantId) {
      dispatch(fetchStoresByMerchant(merchantId));
    } else {
      dispatch(clearStores());
    }
  }, [merchantId, dispatch]);

  useEffect(() => {
    if (open) {
      if (initialDevice) {
        form.setFieldsValue({
          name: initialDevice.name,
          identifier: initialDevice.identifier,
          type: typeof initialDevice.type === 'object' ? initialDevice.type.id : initialDevice.type,
          merchant: typeof initialDevice.merchant === 'object' ? initialDevice.merchant.id : initialDevice.merchant,
          store: typeof initialDevice.store === 'object' ? initialDevice.store.id : initialDevice.store,
        });
        const merchantIdValue = typeof initialDevice.merchant === 'object' ? initialDevice.merchant.id : initialDevice.merchant;
        if (merchantIdValue) {
          setMerchantId(merchantIdValue);
        }
      } else {
        form.resetFields();
        setMerchantId('');
        setIsUnsavedChanges(false);
      }
    }
  }, [initialDevice, form, open]);

  const handleMerchantChange = (value: string) => {
    setMerchantId(value);
    form.setFieldsValue({ store: undefined });
    setIsUnsavedChanges(true);
  };

  const handleFormChange = () => {
    setIsUnsavedChanges(true);
  };

  const handleCancel = () => {
    if (isUnsavedChanges) {
      setShowConfirmModal(true);
      return;
    }
    form.resetFields();
    setMerchantId('');
    setIsUnsavedChanges(false);
    onCancel();
  };

  const confirmCancel = () => {
    setIsUnsavedChanges(false);
    setShowConfirmModal(false);
    form.resetFields();
    setMerchantId('');
    onCancel();
  };

  const handleSubmit = async (values: DeviceFormData) => {
    try {
      const deviceData = {
        name: values.name,
        identifier: values.identifier,
        type: values.type,
        merchant: values.merchant,
        store: values.store,
      };

      if (isEditMode && initialDevice) {
        await dispatch(updateDevice({ id: initialDevice.id, data: deviceData })).unwrap();
        message.success(MESSAGES.DEVICE_UPDATED);
      } else {
        await dispatch(createDevice(deviceData)).unwrap();
        message.success(MESSAGES.DEVICE_CREATED);
      }
      
      setIsUnsavedChanges(false);
      form.resetFields();
      setMerchantId('');
      onSave();
    } catch (error: any) {
      message.error(error || (isEditMode ? MESSAGES.ERROR_UPDATE : MESSAGES.ERROR_CREATE));
    }
  };

  return (
    <>
      <ModalContainerGlobalStyle $isDark={isDarkMode} />
      <StyledModal
        $isDark={isDarkMode}
        title={isEditMode ? `${LABELS.EDIT_DEVICE} - ${initialDevice?.name}` : LABELS.ADD_DEVICE}
        open={open && !showConfirmModal}
        onCancel={handleCancel}
        maskClosable={!isUnsavedChanges}
        keyboard={!isUnsavedChanges}
        closable={true}
        footer={[
          <CancelButton
            key="cancel"
            onClick={handleCancel}
            disabled={loading.create || loading.update}
          >
            {LABELS.CANCEL}
          </CancelButton>,
          <PrimaryButton
            key="submit"
            type="primary"
            icon={<FontAwesomeIcon icon={faSave} />}
            loading={loading.create || loading.update}
            onClick={() => form.submit()}
          >
            {loading.create || loading.update ? LABELS.SAVING : LABELS.SAVE}
          </PrimaryButton>,
        ]}
        width={600}
        destroyOnClose
        afterClose={() => {
          form.resetFields();
          setMerchantId('');
          setIsUnsavedChanges(false);
        }}
      >
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          initialValues={{
            name: initialDevice?.name || '',
            identifier: initialDevice?.identifier || '',
            type: undefined,
            merchant: undefined,
            store: undefined,
          }}
        >
          <StyledFormItem>
            <Form.Item
              name="name"
              label={<StyledFormLabel $isDark={isDarkMode}>{LABELS.NAME}</StyledFormLabel>}
              rules={[{ required: true, message: VALIDATION_MESSAGES.NAME_REQUIRED }]}
            >
              <StyledInput $isDark={isDarkMode} placeholder={LABELS.NAME} />
            </Form.Item>
          </StyledFormItem>

          <StyledFormItem>
            <Form.Item
              name="identifier"
              label={<StyledFormLabel $isDark={isDarkMode}>{LABELS.IDENTIFIER}</StyledFormLabel>}
              rules={[{ required: true, message: VALIDATION_MESSAGES.IDENTIFIER_REQUIRED }]}
            >
              <StyledInput $isDark={isDarkMode} placeholder={LABELS.IDENTIFIER} />
            </Form.Item>
          </StyledFormItem>

          <StyledFormItem>
            <Form.Item
              name="type"
              label={<StyledFormLabel $isDark={isDarkMode}>{LABELS.DEVICE_TYPE}</StyledFormLabel>}
              rules={[{ required: true, message: VALIDATION_MESSAGES.TYPE_REQUIRED }]}
            >
              <StyledSelect $isDark={isDarkMode} placeholder={PLACEHOLDERS.SELECT_TYPE}>
                {deviceTypes.map((type) => (
                  <Select.Option key={type.id} value={type.id}>
                    {type.name}
                  </Select.Option>
                ))}
              </StyledSelect>
            </Form.Item>
          </StyledFormItem>

          <StyledFormItem>
            <Form.Item
              name="merchant"
              label={<StyledFormLabel $isDark={isDarkMode}>{LABELS.MERCHANT}</StyledFormLabel>}
            >
              <StyledSelect 
                $isDark={isDarkMode}
                placeholder={PLACEHOLDERS.SELECT_MERCHANT}
                onChange={handleMerchantChange}
              >
                {merchants.map((merchant) => (
                  <Select.Option key={merchant.id} value={merchant.id}>
                    {merchant.name}
                  </Select.Option>
                ))}
              </StyledSelect>
            </Form.Item>
          </StyledFormItem>

          {(merchantId || (initialDevice && typeof initialDevice.store === 'object' && initialDevice.store?.id)) && (
            <StyledFormItem>
              <Form.Item
                name="store"
                label={<StyledFormLabel $isDark={isDarkMode}>{LABELS.STORE}</StyledFormLabel>}
              >
                <StyledSelect $isDark={isDarkMode} placeholder={PLACEHOLDERS.SELECT_STORE}>
                  {stores.map((store) => (
                    <Select.Option key={store.id} value={store.id}>
                      {store.name}
                    </Select.Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </StyledFormItem>
          )}
          </Form>
        </ConfigProvider>
      </StyledModal>

      <StyledModal
        $isDark={isDarkMode}
        title={LABELS.UNSAVED_CHANGES}
        open={showConfirmModal}
        onOk={confirmCancel}
        onCancel={() => {
          setShowConfirmModal(false);
        }}
        okText={LABELS.YES}
        cancelText={LABELS.NO}
        maskClosable={false}
      >
        <p>{MESSAGES.UNSAVED_CHANGES}</p>
      </StyledModal>
    </>
  );
};
